import { router, Stack, useLocalSearchParams } from 'expo-router';
import * as React from 'react';
import { ActivityIndicator, View } from 'react-native';

import { EventForm, type EventEditorCommunity } from '@/components/events/event-form';
import { useSession } from '@/components/session-provider';
import { useTenant } from '@/components/tenant-provider';
import { getMembershipRole, memberTenantsForUser } from '@/lib/tenant';

export default function MemberNewEventScreen() {
  const { tenant: requestedTenant } = useLocalSearchParams<{ tenant?: string }>();
  const { isAdmin, isProfileLoading, session } = useSession();
  const { isTenantLoading, memberships, tenants } = useTenant();
  const [selectedTenantId, setSelectedTenantId] = React.useState<string | null>(null);

  const communities = React.useMemo<EventEditorCommunity[]>(
    () =>
      memberTenantsForUser(tenants, memberships, isAdmin).map(({ id, name, slug }) => ({
        id,
        name,
        slug,
      })),
    [isAdmin, memberships, tenants]
  );
  const requestedCommunity = communities.find((community) => community.slug === requestedTenant);
  const effectiveTenantId =
    selectedTenantId ??
    requestedCommunity?.id ??
    (communities.length === 1 ? communities[0].id : null);
  const selectedCommunity =
    communities.find((community) => community.id === effectiveTenantId) ?? null;
  const membershipRole = getMembershipRole(memberships, selectedCommunity?.id, session?.user.id);
  const managementLevel =
    isAdmin || membershipRole === 'admin' || membershipRole === 'owner' ? 'operator' : 'member';

  function selectTenant(tenantId: string) {
    const community = communities.find((item) => item.id === tenantId);
    setSelectedTenantId(tenantId);
    if (community) router.setParams({ tenant: community.slug });
  }

  if (isProfileLoading || isTenantLoading) {
    return (
      <View className="flex-1 items-center justify-center bg-background">
        <Stack.Screen options={{ headerShown: false }} />
        <ActivityIndicator />
      </View>
    );
  }

  return (
    <EventForm
      communities={communities}
      managementLevel={managementLevel}
      onClose={() => router.back()}
      onSaved={(savedEvent) => {
        router.replace({ pathname: '/app/events/[id]', params: { id: savedEvent.id } });
      }}
      onTenantChange={selectTenant}
      personalEvent={communities.length === 0}
      tenantId={selectedCommunity?.id ?? null}
      tenantName={selectedCommunity?.name ?? null}
      tenantSlug={selectedCommunity?.slug ?? null}
    />
  );
}
